import React, { useState, useEffect, useCallback } from "react";
import { ToastContainer, toast } from "react-toastify";
import CryptoJS from "crypto-js";
import "react-toastify/dist/ReactToastify.css";
import "./styles/brand.css";

// Import AWS SDK packages for SigV4 authentication
import { SignatureV4 } from "@aws-sdk/signature-v4";
import { Sha256 } from "@aws-crypto/sha256-js";
import { parseUrl } from "@aws-sdk/url-parser";
import { HttpRequest } from "@aws-sdk/protocol-http";

function App() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [config, setConfig] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(true);
  const [urlParams, setUrlParams] = useState({
    appId: null,
    parcelObjectId: null,
  });

  // AWS credentials from environment variables
  /*  const credentials = {
    accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
    secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY
  }; */

  // Effect to parse URL parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const appId = queryParams.get("appId");
    const parcelObjectId = queryParams.get("parcelObjectId");

    setUrlParams({
      appId,
      parcelObjectId,
    });

    if (!appId || !parcelObjectId) {
      console.warn(
        "Missing required URL parameters: appId and/or parcelObjectId"
      );
    }
  }, []);

  // Effect to load configuration
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/config.json`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `HTTP error! status: ${response.status} while fetching config.json`
          );
        }
        return response.json();
      })
      .then((data) => {
        setConfig(data);
        if (data && data.theme) {
          for (const key in data.theme) {
            const cssVarName = `--dynamic-${key.replace(
              /([A-Z])/g,
              (g) => `-${g[0].toLowerCase()}`
            )}`;
            document.documentElement.style.setProperty(
              cssVarName,
              data.theme[key]
            );
          }
        }
        if (data && data.pageTitle) {
          document.title = data.pageTitle;
        }
        if (data && data.faviconPath) {
          const faviconLink = document.querySelector("link[rel*='icon']");
          if (faviconLink) {
            faviconLink.href = `${process.env.PUBLIC_URL}/${data.faviconPath}`;
          }
        }
        setIsConfigLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load or apply config.json:", err);
        toast.error(
          `Configuration error: ${err.message}. Using default styles.`,
          { autoClose: 7000 }
        );
        setIsConfigLoading(false);
      });
  }, []); // Runs once on mount

  const handleGenerateReport = useCallback(async () => {
    setIsLoading(true);
    setError("");
    toast.info("Initiating report generation...", {
      position: "top-right",
      autoClose: 1500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });

    // Check if required parameters are available
    if (!urlParams.appId || !urlParams.parcelObjectId) {
      setError(
        "Missing required parameters. Please ensure the appId and parcelObjectId are provided in the URL."
      );
      toast.error("Missing required parameters", {
        position: "top-right",
        autoClose: 5000,
      });
      setIsLoading(false);
      return;
    }

    // Fetch report from Lambda function
    try {
      // Use the API endpoint from config if available
      const apiEndpoint = process.env.REACT_APP_LAMBDA_FUNCTION_URL;

      // Step 1: Call the Lambda function to initiate report generation using SigV4 authentication
      const initiateResponse = await invokeLambdaWithSigV4(
        {
          appId: urlParams.appId,
          parcelObjectId: urlParams.parcelObjectId,
        },
        apiEndpoint
      );

      // When using SigV4 authentication, the response is already parsed in invokeLambdaWithSigV4
      const initiateData = initiateResponse;

      // The Lambda function returns a response with a body field that contains the actual response
      let responseBody = initiateData;

      // If the response has a body field that's a string, parse it
      if (initiateData.body && typeof initiateData.body === "string") {
        try {
          responseBody = JSON.parse(initiateData.body);
        } catch (error) {
          console.error("Error parsing Lambda response body:", error);
        }
      }

      if (!responseBody.statusKey) {
        console.error("Response body:", responseBody);
        throw new Error("No status key returned from the server");
      }

      // Update toast to indicate polling has begun
      toast.info("Report generation in progress...", {
        position: "top-right",
        autoClose: 2000,
      });

      // Step 2: Poll for status until completion or failure
      const maxPollingAttempts = 30; // Default to 30 attempts
      const pollingInterval = 2000; // Default to 2 seconds
      let currentAttempt = 0;
      let statusData = null;

      const pollStatus = async () => {
        if (currentAttempt >= maxPollingAttempts) {
          throw new Error(
            "Report generation timed out. Please try again later."
          );
        }

        currentAttempt++;

        // Make status check request with SigV4 authentication
        const statusResponseData = await invokeLambdaWithSigV4(
          {
            checkStatus: true,
            statusKey: responseBody.statusKey,
          },
          apiEndpoint
        );

        // Parse the response body if it's a string
        let statusData = statusResponseData;
        if (
          statusResponseData.body &&
          typeof statusResponseData.body === "string"
        ) {
          try {
            statusData = JSON.parse(statusResponseData.body);
          } catch (error) {
            console.error("Error parsing status response body:", error);
          }
        }

        // Check the status
        if (statusData.status === "PROCESSING") {
          // Continue polling after interval
          return new Promise((resolve) =>
            setTimeout(() => resolve(pollStatus()), pollingInterval)
          );
        } else if (
          statusData.status === "COMPLETED" &&
          statusData.downloadUrl
        ) {
          // Report is ready, proceed to download
          return statusData;
        } else if (statusData.status === "FAILED") {
          throw new Error(
            `Report generation failed: ${statusData.error || "Unknown error"}`
          );
        } else {
          console.error("Invalid status response:", statusData);
          throw new Error("Invalid status response from server");
        }
      };

      // Start polling
      statusData = await pollStatus();

      // Step 3: Fetch the PDF using the pre-signed URL
      const pdfResponse = await fetch(statusData.downloadUrl, {
        headers: {
          Accept: "application/pdf",
        },
      });

      if (!pdfResponse.ok) {
        throw new Error("Failed to fetch PDF from pre-signed URL");
      }

      const blob = await pdfResponse.blob();

      // Create a temporary link to trigger the download
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);

      // Extract the original filename from the S3 URL or use a default
      let filename = "report.pdf";

      // Try to extract the filename from the S3 URL
      if (statusData.s3Url) {
        // Extract the filename from the S3 URL (last part after the last slash)
        const s3UrlParts = statusData.s3Url.split("/");
        if (s3UrlParts.length > 0) {
          const lastPart = s3UrlParts[s3UrlParts.length - 1];
          if (lastPart && lastPart.endsWith(".pdf")) {
            filename = lastPart;
          }
        }
      }

      // Use the original filename from S3
      link.download = filename;

      // Append to body and click
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(link.href);

      // Show success toast
      toast.success("Report downloaded successfully!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setIsLoading(false);
    } catch (err) {
      console.error("Error generating or downloading report:", err);
      toast.error("Failed to generate or download the report.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setError(
        `Failed to generate or download the report. Please try again. If you've received multiple errors please contact mapinsights@timmons.com. (Error: ${err.message})`
      );
      setIsLoading(false);
    }
  }, [config, urlParams, setIsLoading, setError]);

  // Automatically trigger report generation after page load
  useEffect(() => {
    // Ensure handleGenerateReport is defined, config is loaded, and URL parameters are available
    if (
      !isConfigLoading &&
      config &&
      typeof handleGenerateReport === "function" &&
      urlParams.appId &&
      urlParams.parcelObjectId
    ) {
      const timer = setTimeout(() => {
        handleGenerateReport();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isConfigLoading, config, handleGenerateReport, urlParams]); // Dependencies updated

  if (isConfigLoading) {
    return (
      <div
        className="loading-container"
        style={{ padding: "20px", textAlign: "center" }}
      >
        Loading configuration...
      </div>
    );
  }

  if (!config) {
    return (
      <div
        className="error-container"
        style={{ padding: "20px", textAlign: "center", color: "red" }}
      >
        Failed to load application configuration. Please check the console and
        try refreshing the page.
      </div>
    );
  }

  return (
    <div className="App brand-container">
      <header className="brand-header">
        <div className="header-content">
          {config && config.logo && config.logo.path && (
            <img
              src={`${process.env.PUBLIC_URL}/${config.logo.path}`}
              alt={config.logo.altText || "Application Logo"}
              className="header-logo"
              style={{ height: config.logo.height || "40px" }}
            />
          )}
          <h1 className="brand-text-primary">
            {config && config.pageTitle ? config.pageTitle : "Map Insights"}
          </h1>
        </div>
      </header>
      <main className="brand-section">
        {!urlParams.appId || !urlParams.parcelObjectId ? (
          <div
            className="brand-error"
            style={{
              marginBottom: "20px",
              padding: "15px",
              borderRadius: "5px",
            }}
          >
            Missing required URL parameters (appId and/or parcelObjectId).
            Please ensure you're accessing this page from the Experience Builder
            application.
          </div>
        ) : null}

        <button
          className="brand-button"
          onClick={handleGenerateReport}
          disabled={isLoading || !urlParams.appId || !urlParams.parcelObjectId}
          style={{
            cursor:
              !urlParams.appId || !urlParams.parcelObjectId
                ? "not-allowed"
                : "pointer",
          }}
          onMouseEnter={(e) =>
            (e.target.style.transform =
              !urlParams.appId || !urlParams.parcelObjectId
                ? "none"
                : "scale(1.05)")
          }
          onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
        >
          {isLoading ? "Generating..." : "Generate Report"}
        </button>

        {isLoading && (
          <div
            style={{
              marginTop: "var(--spacing-lg)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              className="brand-spinner"
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            ></div>
          </div>
        )}

        {error && <div className="brand-error">{error}</div>}
      </main>
      <ToastContainer />
    </div>
  );
}

/**
 * Invokes a Lambda function URL with AWS SigV4 authentication
 *
 * @param {Object} payload - The payload to send to the Lambda function
 * @param {string} lambdaUrl - The Lambda function URL
 * @returns {Promise<Object>} - The response from the Lambda function
 */
async function invokeLambdaWithSigV4(payload, lambdaUrl) {
  try {
    // Parse the URL
    const parsedUrl = parseUrl(lambdaUrl);

    // Create the HTTP request
    const request = new HttpRequest({
      hostname: parsedUrl.hostname,
      path: parsedUrl.pathname,
      protocol: parsedUrl.protocol,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Host: parsedUrl.hostname,
      },
      body: JSON.stringify(payload),
    });

    // Get AWS credentials from environment variables
    
    const credentials = {
      accessKeyId: CryptoJS.AES.decrypt(process.env.REACT_APP_AK, process.env.REACT_APP_PP).toString(CryptoJS.enc.Utf8),
      secretAccessKey: CryptoJS.AES.decrypt(process.env.REACT_APP_SK, process.env.REACT_APP_PP).toString(CryptoJS.enc.Utf8),
    };

    // Create the signature
    const signer = new SignatureV4({
      credentials,
      region: process.env.REACT_APP_AWS_REGION,
      service: "lambda",
      sha256: Sha256,
    });

    // Sign the request
    const signedRequest = await signer.sign(request);

    // Convert headers to a format that fetch can use
    const headers = {};
    Object.entries(signedRequest.headers).forEach(([key, value]) => {
      headers[key] = value;
    });

    // Add Origin header to help with CORS
    headers["Origin"] = window.location.origin;

    // Log the approach
    const requestUrl = `${signedRequest.protocol}//${signedRequest.hostname}${signedRequest.path}`;

    const response = await fetch(requestUrl, {
      method: signedRequest.method,
      headers: headers,
      body: signedRequest.body,
    });

    // Process the response
    const responseText = await response.text();

    try {
      // Try to parse the response as JSON
      return JSON.parse(responseText);
    } catch (e) {
      // If the response is not JSON, return the text
      return responseText;
    }
  } catch (error) {
    console.error("Error invoking Lambda function URL:", error);
    throw error;
  }
}

export default App;
